import React from 'react';
import moment from 'moment';
import get from 'lodash/get';
import objectScan from 'object-scan';

import nextBlogPostsIndex from '../../www-next/content/blog/__index.json';

// -- Gather and transform Next blog posts
//
export const gatherNextBlogPosts = () =>
  nextBlogPostsIndex.items.map((item) => ({
    slug: item.full_slug.split('/')[1],
    Author: item.content.author,
    Categories: item.content.categories,
    Date: new Date(item.content.date),
    Description: item.content.description,
    Tags: item.content.tags.value,
    Title: item.content.title,
    PreviewImage: {
      publicURL: item.content.preview_image.filename
    }
  }));

export const sortBlogPosts = (items) =>
  items.sort(function (a, b) {
    return new Date(b.Date) - new Date(a.Date);
  });

// -- Dates and times...
//
export const supportedDateFormats = [
  'DD/MM/YY',
  'DD/MM/YYYY',
  'Do MMMM YYYY',
  'DD/MM/YY HH:mm',
  'DD/MM/YYYY HH:mm',
  'YYYY-MM-DDTHH:mm:ss.SSSZ'
];

export const parseDateOrDateTime = (d) => moment(d, supportedDateFormats);
export const formatDateOrDateTime = (d) => parseDateOrDateTime(d).format();
export const formatDateForBlog = (d) => parseDateOrDateTime(d).format('Do MMMM YYYY');

// -- Mappers and formatters...
//
export const slugify = (garbage) => garbage.toLowerCase().replace(/\s/g, '-');

export const authorUrl = (author) => {
  const slug = slugify(author);
  return `/blog/author/${slug}/`;
};

// -- Templates...
//
export const initialiseTemplate = (data) => {
  const source = JSON.parse(data.yaml.fields.source);

  const meta = data.yaml.Meta;

  const content = data.allMarkdownRemark.edges.reduce((acc, { node }) => {
    const [fileName] = node.fileAbsolutePath.split('/').reverse();
    acc[fileName] = node.html;

    return acc;
  }, {});

  const assets = data.allFile.edges.reduce((acc, { node }) => {
    acc[node.relativePath] = node.publicURL;
    return acc;
  }, {});

  const pathName = data.yaml.fields.slug;

  return [source, meta, content, assets, pathName];
};

// -- Blog utils...
//
const blogImageSrc = (post) => get(post.PreviewImage, 'publicURL') || get(post.FeaturedImage, 'publicURL');
const resolveBlogImageSrcPath = (image, prefix) => (image.startsWith('/') ? image : `${prefix}/${image}`);
const blogImageSrcForBlogIndex = (post) => resolveBlogImageSrcPath(blogImageSrc(post), post.slug);

export const gatherBlogPosts = (edges) =>
  edges
    .filter(({ node }) => node.fields.layout === 'BlogPost')
    .map(({ node }) => ({
      ...node.Meta,
      slug: node.fields.slug.replace(/^(\/blog\/)/, '').replace(/\/$/, ''),
      image: blogImageSrcForBlogIndex(node.Meta)
    }));

// -- Business guide utils...
//
export const gatherBusinessGuides = (edges) =>
  edges
    .filter(({ node }) => node.fields.layout === 'BusinessGuide')
    .map(({ node }) => ({
      ...node.Meta,
      slug: node.fields.slug.replace(/^(\/resources\/business-guides\/)/, '').replace(/\/$/, '')
    }));

// -- Make Pocket Money Equal blog post utils
//
export const gatherMpmeBlogPosts = (edges) =>
  edges
    .filter(({ node }) => node.fields.layout === 'MpmeBlogPost')
    .map(({ node }) => ({
      ...node.Meta,
      slug: node.fields.slug
        .replace(/^(\/campaign\/makemoneyequal\/closing-the-gender-play-gap\/)/, '')
        .replace(/\/$/, '')
    }));

// -- Query string utils...
//
export const queryString = (search) => {
  const queryString = search.slice(1);
  return queryString.split('&').reduce((acc, curr) => {
    const [k, v] = curr.split('=');
    return { ...acc, [k]: v };
  }, {});
};

// -- Image utils...
//
export const fixedImage = (data, originalPath) =>
  data.allFile.nodes.find((node) => node.absolutePath.endsWith(originalPath)).childImageSharp.gatsbyImageData;
export const fluidImage = (data, originalPath) =>
  data.allFile.nodes.find((node) => node.absolutePath.endsWith(originalPath)).childImageSharp.gatsbyImageData;

// -- Content utils...
//
export const parseLine = (line) => {
  return (
    line
      // Stick markers either side of any http(s) link
      .replace(/<a\shref="http[^<]*<\/a>/g, '***$&***')
      // Split out the links
      .split('***')
      // Turn the string links into real JSX links
      .map((phrase, index) => {
        if (!phrase.startsWith('<a href="http')) return phrase;
        const [, href, text] = phrase.match(/<a href="([^"]*)">(.*)<\/a>/);
        return (
          <a key={`${href}_${index}`} href={href} target="_blank" rel="noopener noreferrer">
            {text}
          </a>
        );
      })
  );
};

// -- Currency utils...
//
export const convertNumberToCurrency = (number, locale, symbol) => {
  return Number(number).toLocaleString(locale, {
    style: 'currency',
    currency: symbol
  });
};

/**
 * Does this page use the OnboardingCta, meaning visitors will be sent to the new onboarding flow
 *
 * @param {array} source A collection of components which make up the page
 * @returns {string | false} If the components includes the 'OnboardingCta' reuturn the route param, else false
 */
export const fetchWebflowRoute = (source) => {
  const pageOnboardingCta = objectScan(['**.OnboardingCta'], {
    rtn: 'value'
  })(source);

  if (pageOnboardingCta.length === 0) return false;
  return pageOnboardingCta[0].route || 'default';
};

/**
 *
 * @param {array} source A collection of components which make up the page
 * @returns {bool} If the components include any that require Google Recaptcha
 */
export const sourceRequiresRecaptcha = (source) => {
  return objectScan(['**.(OnboardingForm|ReferralsForm|ReferralsFormAccountants)'], {
    rtn: 'bool'
  })(source);
};

/**
 * Track an event through Google Tag Manager.
 *
 * @param {object} eventObject A JSON object containing all the tracking event info, to be pushed into the data layer.
 */
export const trackGtm = (eventObject) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(eventObject);
  }
};

// -- Avoid sticky header when scrolling to anchor
//
const anchorScrollAction = (element, headerHeight, navWide) => {
  const elementPosY = element.getBoundingClientRect().top;
  const padding = navWide ? 30 : 20;
  const adjustment = headerHeight + padding;
  const newScrollPosition = elementPosY + window.scrollY - adjustment;
  window.scrollTo({
    top: newScrollPosition,
    behavior: 'smooth'
  });
};

// Adjust anchor scroll on page load
export const adjustAnchorScrollOnLoad = (element, navWide) => {
  const headerHeight = navWide ? 144 : 68;
  setTimeout(() => {
    anchorScrollAction(element, headerHeight, navWide);
  }, 500);
};

// Scroll to in-page anchor links
export const anchorLinkScroll = (element, navWide) => {
  const headerHeight = navWide ? 144 : 68;
  anchorScrollAction(element, headerHeight, navWide);
};
